import { ExternalLinkIcon, SearchIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";

import { shouldSuppressValueDropdown } from "../config/schema-utils";
import { getFilterDefinitionByKey } from "../config/structured-search-schema";
import { useStructuredSearchStore } from "../stores";

import AutocompleteList from "./AutocompleteList";
import AutocompleteSuggestionsRouter from "./AutocompleteSuggestionsRouter";
import { selectSuggestion } from "./selectSuggestion";
import { useStructuredSuggestions } from "./useStructuredSuggestions";
import type { SuggestionItem } from "./types";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";

export interface AutocompleteDropdownProps {
  isEditorActive?: boolean;
  onExploreInTable?: () => void;
  onExploreInTablePreload?: () => void;
}

const SMOOTH_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: "smooth",
  block: "nearest",
};

const scrollHighlightedGroupIntoView = (list: HTMLUListElement) => {
  const highlightedOption = list.querySelector<HTMLElement>(
    '[role="option"][aria-selected="true"]',
  );
  if (!highlightedOption) {
    return;
  }

  const highlightedGroup = highlightedOption.dataset.groupName;
  if (!highlightedGroup) {
    highlightedOption.scrollIntoView(SMOOTH_SCROLL_OPTIONS);
    return;
  }

  const groupedElements = Array.from(
    list.querySelectorAll<HTMLElement>("[data-group-name]"),
  ).filter(element => element.dataset.groupName === highlightedGroup);
  const groupStartElement =
    groupedElements.find(element => element.dataset.groupRole === "header") ?? groupedElements[0];
  const groupEndElement = [...groupedElements]
    .reverse()
    .find(element => element.dataset.groupRole === "option");

  if (!groupStartElement || !groupEndElement) {
    highlightedOption.scrollIntoView(SMOOTH_SCROLL_OPTIONS);
    return;
  }

  const groupTop = groupStartElement.offsetTop;
  const groupBottom = groupEndElement.offsetTop + groupEndElement.offsetHeight;
  const visibleTop = list.scrollTop;
  const visibleBottom = visibleTop + list.clientHeight;
  const groupHeight = groupBottom - groupTop;

  if (groupHeight <= list.clientHeight) {
    if (groupTop < visibleTop) {
      list.scrollTo({ top: groupTop, behavior: "smooth" });
      return;
    }

    if (groupBottom > visibleBottom) {
      list.scrollTo({ top: groupBottom - list.clientHeight, behavior: "smooth" });
      return;
    }
  }

  highlightedOption.scrollIntoView(SMOOTH_SCROLL_OPTIONS);

  if (groupStartElement.offsetTop < list.scrollTop) {
    list.scrollTo({ top: groupStartElement.offsetTop, behavior: "smooth" });
  }
};

const AutocompleteDropdown = observer(
  ({
    isEditorActive = false,
    onExploreInTable,
    onExploreInTablePreload,
  }: AutocompleteDropdownProps) => {
    const store = useStructuredSearchStore();
    const listRef = useRef<HTMLUListElement>(null);
    const {
      suggestions,
      disabledFieldSuggestions,
      lockedFieldSuggestions,
      disabledOperatorSuggestions,
      disabledPlainTextSearchSuggestion,
      unavailableSuggestionsReason,
      nestedKeyMode,
      isPromptField,
      isTraceVersionedEntityField,
      promptVersionStep,
      isLoadingSuggestions,
      isFetchingSuggestions,
      isAwaitingCurrentInputSuggestions,
    } = useStructuredSuggestions();
    const suppressDropdown =
      store.step === "value" &&
      shouldSuppressValueDropdown(store.schema, store.inProgress.field, store.inProgress.operator);
    const hasUnavailableSuggestionsSection =
      (store.step === "field" &&
        (Boolean(disabledPlainTextSearchSuggestion) ||
          disabledFieldSuggestions.length > 0 ||
          lockedFieldSuggestions.length > 0)) ||
      (store.step === "operator" && disabledOperatorSuggestions.length > 0);
    const shouldShowSuggestionsLoading =
      isAwaitingCurrentInputSuggestions ||
      ((isLoadingSuggestions || isFetchingSuggestions) &&
        suggestions.length === 0 &&
        !hasUnavailableSuggestionsSection);
    const displayedSuggestions = useMemo(
      () => (shouldShowSuggestionsLoading ? [] : suggestions),
      [shouldShowSuggestionsLoading, suggestions],
    );
    const highlightedIndex = Math.min(
      store.keyboardIndex,
      Math.max(0, displayedSuggestions.length - 1),
    );
    const showEmptySuggestionsState =
      displayedSuggestions.length === 0 && !hasUnavailableSuggestionsSection;
    const showSuggestionsLoading = showEmptySuggestionsState && shouldShowSuggestionsLoading;
    const canApplyCurrentFilter =
      store.step === "value" &&
      (!store.isMultiSelectMode ||
        store.multiValues.length > 0 ||
        Boolean(store.inputText.trim()) ||
        Boolean(store.inProgress.value));
    const keybindHints = [
      { keyLabel: "Esc", description: "cancel" },
      { keyLabel: "↑ ↓", description: "move" },
      { keyLabel: "Enter", description: "select" },
      ...(store.step !== "field" ? [{ keyLabel: "Shift + Backspace", description: "back" }] : []),
      ...(store.step === "value" && store.isMultiSelectMode
        ? [{ keyLabel: "Cmd + Enter", description: "apply" }]
        : []),
    ];
    const isPlainTextSearchSuggestion = (
      item: SuggestionItem,
    ): item is Extract<SuggestionItem, { type: "plain_text_search" }> =>
      "type" in item && item.type === "plain_text_search";

    useEffect(() => {
      if (listRef.current && displayedSuggestions.length > 0) {
        scrollHighlightedGroupIntoView(listRef.current);
      }
    }, [highlightedIndex, displayedSuggestions.length]);

    const handleSelect = useCallback(
      (index: number) => {
        const items = displayedSuggestions;
        const item = items[index];
        if (!item) {
          return;
        }
        if (isPlainTextSearchSuggestion(item)) {
          store.setSearch(item.value);
          store.cancelBuilder();
          return;
        }
        selectSuggestion(store, item, {
          nestedKeyMode,
          isPromptField,
          isTraceVersionedEntityField,
          versionStep: promptVersionStep,
        });
      },
      [
        displayedSuggestions,
        isPromptField,
        isTraceVersionedEntityField,
        nestedKeyMode,
        promptVersionStep,
        store,
      ],
    );

    const handleApply = useCallback(() => {
      store.completeFilter();
    }, [store]);

    if (!isEditorActive || !store.dropdownOpen || suppressDropdown) {
      return null;
    }

    return (
      <AutocompleteList
        listRef={listRef}
        footer={
          <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-2.5 text-xs text-slate-500">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {keybindHints.map(hint => (
                  <span key={hint.keyLabel} className="flex items-center gap-1.5">
                    <span className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 font-medium text-slate-700 shadow-sm shadow-slate-200/70">
                      {hint.keyLabel}
                    </span>
                    <span>{hint.description}</span>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {onExploreInTable && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onMouseDown={event => event.preventDefault()}
                    onPointerEnter={onExploreInTablePreload}
                    onFocus={onExploreInTablePreload}
                    onClick={onExploreInTable}
                    className="h-7 gap-1.5 px-2.5 text-xs"
                  >
                    Explore in Table
                    <ExternalLinkIcon className="h-3.5 w-3.5" />
                  </Button>
                )}
                {store.step === "value" && store.isMultiSelectMode && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canApplyCurrentFilter}
                    onMouseDown={event => event.preventDefault()}
                    onClick={handleApply}
                    className="h-7 px-2.5 text-xs"
                  >
                    Apply
                  </Button>
                )}
              </div>
            </div>
          </div>
        }
      >
        {showEmptySuggestionsState && (
          <li className="min-h-28 px-4 py-3 text-sm text-slate-500">
            {showSuggestionsLoading ? (
              <div className="flex min-h-24 flex-col items-center justify-center gap-2 text-center text-slate-500">
                <LoadingSpinner size={5} className="mr-0 text-slate-400" />
                <span>Loading suggestions...</span>
              </div>
            ) : (
              <div className="flex min-h-24 flex-col items-center justify-center gap-2 text-center text-slate-500">
                <div className="rounded-full bg-slate-100 p-2.5">
                  <SearchIcon className="h-5 w-5 text-slate-400" />
                </div>
                <span>No results</span>
              </div>
            )}
          </li>
        )}
        <AutocompleteSuggestionsRouter
          step={store.step}
          suggestions={displayedSuggestions}
          disabledFieldSuggestions={disabledFieldSuggestions}
          lockedFieldSuggestions={lockedFieldSuggestions}
          disabledOperatorSuggestions={disabledOperatorSuggestions}
          disabledPlainTextSearchSuggestion={disabledPlainTextSearchSuggestion}
          unavailableSuggestionsReason={unavailableSuggestionsReason}
          highlightedIndex={highlightedIndex}
          onSelect={handleSelect}
          filterDefinition={getFilterDefinitionByKey(store.schema, store.inProgress.field)}
          isPromptField={isPromptField}
          isTraceVersionedEntityField={isTraceVersionedEntityField}
          isMultiSelectMode={store.isMultiSelectMode}
          multiValues={store.multiValues}
          promptVersionStep={promptVersionStep}
          currentEntityId={store.inProgress.promptId ?? store.inProgress.entityId}
        />
      </AutocompleteList>
    );
  },
);

export default AutocompleteDropdown;
